import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ConnectCard } from './Lore.js';

/**
 * Getting Started pages — one per VCS section in the sidebar
 * (/getting-started/perforce, /getting-started/lore). Static onboarding
 * content mirroring the repo docs (README "For developers",
 * docs/lore-workstation-setup.md); if connection strings or install
 * steps change there, change them here too.
 *
 * Install steps are per-OS behind a segmented picker that defaults to
 * the visitor's detected platform, so the common case is zero clicks.
 * The choice persists in localStorage.
 */

const OSES = ['windows', 'macos', 'linux'] as const;
type Os = (typeof OSES)[number];
const OS_LABEL: Record<Os, string> = { windows: 'Windows', macos: 'macOS', linux: 'Linux' };
const STORAGE_KEY = 'theia.gettingStarted.os';

function detectOs(): Os {
  const ua = navigator.userAgent;
  if (/Win/i.test(ua)) return 'windows';
  if (/Linux|X11/i.test(ua) && !/Android/i.test(ua)) return 'linux';
  return 'macos';
}

function useOsSelection(): [Os, (os: Os) => void] {
  const [selected, setSelected] = useState<Os>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      // Tolerate the previous multi-select shape (a JSON array) so an
      // existing visitor's stored choice still resolves to something.
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      const candidate = Array.isArray(parsed) ? parsed[0] : parsed;
      if (typeof candidate === 'string' && (OSES as readonly string[]).includes(candidate)) {
        return candidate as Os;
      }
    } catch {
      /* fall through to detection */
    }
    return detectOs();
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selected));
    } catch {
      /* storage unavailable — selection is still live for this session */
    }
  }, [selected]);

  return [selected, setSelected];
}

/** Segmented control: [ Windows | macOS | Linux ], one active. */
function OsPicker({ selected, onSelect }: { selected: Os; onSelect: (os: Os) => void }) {
  return (
    <div
      role="radiogroup"
      aria-label="Operating system"
      className="inline-flex shrink-0 overflow-hidden rounded-md border border-border bg-card"
    >
      {OSES.map((os, i) => (
        <button
          key={os}
          type="button"
          role="radio"
          aria-checked={selected === os}
          onClick={() => onSelect(os)}
          className={cn(
            'px-2.5 py-1 text-xs transition-colors',
            i > 0 && 'border-l border-border',
            selected === os
              ? 'bg-neutral-800 font-medium text-foreground'
              : 'text-muted-foreground hover:bg-neutral-800/50 hover:text-foreground',
          )}
        >
          {OS_LABEL[os]}
        </button>
      ))}
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded bg-muted/40 px-3 py-2 pr-14 font-mono text-[11px] leading-relaxed">
        {children}
      </pre>
      {/* Opaque chip, not bare text: long unwrapped lines scroll UNDER
          this button inside the overflow container, and transparent
          text-on-text is unreadable. */}
      <button
        type="button"
        className="absolute right-1.5 top-1.5 rounded border border-border bg-card px-1.5 py-0.5 text-[11px] text-theia-400 shadow-sm hover:bg-neutral-800"
        onClick={() => {
          void navigator.clipboard.writeText(children);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-card px-4 py-3">
      <div className="mb-2 text-sm font-medium">{title}</div>
      <div className="flex flex-col gap-2 text-xs text-muted-foreground">{children}</div>
    </div>
  );
}

/** Per-OS block; renders only when that platform is selected. */
function OsBlock({ os, selected, children }: { os: Os; selected: Os; children: ReactNode }) {
  if (selected !== os) return null;
  return <div className="flex flex-col gap-2">{children}</div>;
}

function PageHeader({
  title,
  selected,
  onSelect,
}: {
  title: string;
  selected: Os;
  onSelect: (os: Os) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
      <OsPicker selected={selected} onSelect={onSelect} />
    </div>
  );
}

export function PerforceGettingStartedPage() {
  const [selected, setOs] = useOsSelection();
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-3 p-4">
      <PageHeader title="Getting started with Perforce" selected={selected} onSelect={setOs} />
      <p className="text-xs text-muted-foreground">
        Perforce (Helix Core) is the version control system this studio ships on: a central
        server holds every revision, you sync a workspace to your machine, check files out to
        edit them, and submit changes back as numbered changelists.
      </p>

      <Card title="1 · Install a client">
        <p>
          You also need a username and temporary password from the admin. GUI client is{' '}
          <a
            href="https://www.perforce.com/downloads/helix-visual-client-p4v"
            target="_blank"
            rel="noreferrer"
            className="text-theia-400 hover:underline"
          >
            P4V
          </a>
          ; the CLI is <code className="font-mono">p4</code>.
        </p>
        <OsBlock os="windows" selected={selected}>
          <p>
            Run the{' '}
            <a
              href="https://www.perforce.com/downloads/helix-visual-client-p4v"
              target="_blank"
              rel="noreferrer"
              className="text-theia-400 hover:underline"
            >
              P4V installer
            </a>{' '}
            (bundles P4V + the <code className="font-mono">p4</code> CLI and puts it on PATH).
            Via winget instead:
          </p>
          <CodeBlock>winget install Perforce.P4V</CodeBlock>
        </OsBlock>
        <OsBlock os="macos" selected={selected}>
          <CodeBlock>{`brew install --cask p4v   # GUI
brew install --cask p4    # CLI only`}</CodeBlock>
        </OsBlock>
        <OsBlock os="linux" selected={selected}>
          <p>
            CLI binary straight from Perforce (no package repo needed):
          </p>
          <CodeBlock>{`curl -o ~/.local/bin/p4 https://filehost.perforce.com/perforce/r25.2/bin.linux26x86_64/p4
chmod +x ~/.local/bin/p4`}</CodeBlock>
          <p>
            P4V for Linux is the{' '}
            <a
              href="https://www.perforce.com/downloads/helix-visual-client-p4v"
              target="_blank"
              rel="noreferrer"
              className="text-theia-400 hover:underline"
            >
              tarball on the same page
            </a>
            .
          </p>
        </OsBlock>
      </Card>

      <Card title="2 · Connect (remote)">
        <p>
          In P4V: Server <code className="font-mono">ssl:p4.theia.io:1669</code>, your username,
          your password. Real CA-signed cert — no trust prompt, no SSO, no proxy. CLI:
        </p>
        <OsBlock os="windows" selected={selected}>
          <CodeBlock>{`p4 set P4PORT=ssl:p4.theia.io:1669
p4 set P4USER=<your-username>
p4 login
p4 info`}</CodeBlock>
        </OsBlock>
        <OsBlock os="macos" selected={selected}>
          <CodeBlock>{`export P4PORT=ssl:p4.theia.io:1669 P4USER=<your-username>
p4 login
p4 info`}</CodeBlock>
        </OsBlock>
        <OsBlock os="linux" selected={selected}>
          <CodeBlock>{`export P4PORT=ssl:p4.theia.io:1669 P4USER=<your-username>
p4 login
p4 info`}</CodeBlock>
        </OsBlock>
      </Card>

      <Card title="3 · First steps">
        <p>
          Create a workspace (P4V: Connection → New Workspace), map the depot paths you need,
          and sync. Browse depots with asset previews right here in the{' '}
          <Link to="/depot" className="text-theia-400 hover:underline">
            Depot
          </Link>{' '}
          tab — <code className="font-mono">.uasset</code> files render 3D/texture/blueprint
          previews without a UE install.
        </p>
      </Card>
    </div>
  );
}

export function LoreGettingStartedPage() {
  const [selected, setOs] = useOsSelection();
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-3 p-4">
      <PageHeader title="Getting started with Lore" selected={selected} onSelect={setOs} />
      <p className="text-xs text-muted-foreground">
        Lore is Epic&apos;s open-source version control system, built for game projects: it
        stores large binary assets natively, dedupes identical content across a repo, and
        hydrates files on demand so a clone doesn&apos;t drag down everything at once.
      </p>

      <Card title="1 · Install the CLI (version 0.8.6 — match the server)">
        <p>
          Skip Epic&apos;s install one-liners (they fetch latest; pre-1.0 protocol churn makes a
          client newer than our server risky). Pin 0.8.6 from the{' '}
          <a
            href="https://github.com/EpicGames/lore/releases/tag/v0.8.6"
            target="_blank"
            rel="noreferrer"
            className="text-theia-400 hover:underline"
          >
            v0.8.6 release
          </a>
          .
        </p>
        <OsBlock os="windows" selected={selected}>
          <p>
            Via scoop — no official bucket exists yet, so use a local manifest (which also pins
            the version). Save as <code className="font-mono">lore.json</code>, then{' '}
            <code className="font-mono">scoop install ./lore.json</code>:
          </p>
          <CodeBlock>{`{
    "version": "0.8.6",
    "description": "Epic Games Lore VCS CLI",
    "homepage": "https://github.com/EpicGames/lore",
    "license": "MIT",
    "architecture": {
        "64bit": {
            "url": "https://github.com/EpicGames/lore/releases/download/v0.8.6/lore-v0.8.6-x86_64-pc-windows-msvc.zip"
        }
    },
    "bin": "lore.exe"
}`}</CodeBlock>
          <p>
            If the shim step can&apos;t find <code className="font-mono">lore.exe</code>, the zip
            nests it — check <code className="font-mono">ls ~/scoop/apps/lore/current</code> and
            point <code className="font-mono">bin</code> at that path. Zero-thought fallback: the{' '}
            <code className="font-mono">lore-setup-v0.8.6-…msi</code> from the release page
            handles PATH itself. Git Bash works throughout —{' '}
            <code className="font-mono">lore://</code> arguments start with{' '}
            <code className="font-mono">lore:</code>, not <code className="font-mono">/</code>,
            so MSYS path mangling never touches them.
          </p>
        </OsBlock>
        <OsBlock os="macos" selected={selected}>
          <p>Apple Silicon build (there is no Intel darwin release):</p>
          <CodeBlock>{`curl -L -o lore.tar.gz https://github.com/EpicGames/lore/releases/download/v0.8.6/lore-v0.8.6-aarch64-apple-darwin.tar.gz
tar xzf lore.tar.gz
xattr -d com.apple.quarantine ./lore   # clear Gatekeeper
mv ./lore ~/.local/bin/`}</CodeBlock>
        </OsBlock>
        <OsBlock os="linux" selected={selected}>
          <CodeBlock>{`curl -L -o lore.tar.gz https://github.com/EpicGames/lore/releases/download/v0.8.6/lore-v0.8.6-x86_64-unknown-linux-gnu.tar.gz
tar xzf lore.tar.gz
mv ./lore ~/.local/bin/`}</CodeBlock>
        </OsBlock>
      </Card>

      <Card title="2 · Connect">
        <p>
          Generate your token below and run the command it gives you (60-day lifetime — come
          back here when it expires). Don&apos;t pass <code className="font-mono">--identity</code>{' '}
          to any lore command.
        </p>
        <ConnectCard />
      </Card>

      <Card title="3 · Clone and push">
        <CodeBlock>{`lore clone lore://10.0.0.6:41337/<Repo> my-workspace
cd my-workspace
# ...edit files...
lore stage --scan .
lore commit "what changed"
lore push`}</CodeBlock>
        <p>
          p4 translation: <code className="font-mono">commit</code> is local,{' '}
          <code className="font-mono">push</code> publishes (submit ≈ commit + push).{' '}
          <code className="font-mono">stage</code> does NOT scan the filesystem unless you pass{' '}
          <code className="font-mono">--scan</code>. <code className="font-mono">lore sync</code>{' '}
          pulls latest. Browse repos in the{' '}
          <Link to="/lore" className="text-theia-400 hover:underline">
            Repos
          </Link>{' '}
          tab.
        </p>
      </Card>

    </div>
  );
}
